/* The flow as the editor holds it: every block and question carries a `uid` of the editor's
   own, so a step keeps its selection, its open menu and its half-typed fields through a
   reorder, an insert or a duplicate — a new one has no `id` or `key` until the server names
   it. `toBlocks` drops the uids for the save. */
import { MAX_BLOCKS, newBlock } from "./blocks";

import type { LoginBlock, OnboardingBlock, PhoneBlock, Question, QuestionsBlock } from "@/shared/types";

export type DraftQuestion = Question & { uid: string };
export type DraftQuestions = Omit<QuestionsBlock, "fields"> & { uid: string; fields: DraftQuestion[] };
export type DraftStep = DraftQuestions | (LoginBlock & { uid: string }) | (PhoneBlock & { uid: string });

let made = 0;
/** A uid no saved block or question has: theirs are their server ids. */
export const newUid = () => `new-${(made += 1)}`;

const withUids = (block: OnboardingBlock, uid: string): DraftStep =>
  block.type === "questions"
    ? { ...block, uid, fields: block.fields.map((q, i) => ({ ...q, uid: q.key ? `${uid}:${q.key}` : `${uid}:${i}` })) }
    : { ...block, uid };

/** A saved flow's blocks, named by their ids. */
export const toSteps = (blocks: OnboardingBlock[]): DraftStep[] =>
  blocks.map((block, i) => withUids(block, block.id ?? `saved-${i}`));

/** Blocks the server hasn't named: a template's, a new one, a copy. */
export const freshSteps = (blocks: OnboardingBlock[]): DraftStep[] => blocks.map((block) => withUids(block, newUid()));

/** The flow as a save sends it. */
export const toBlocks = (steps: DraftStep[]): OnboardingBlock[] =>
  steps.map(({ uid, ...step }) =>
    step.type === "questions" ? { ...step, fields: step.fields.map(({ uid: _uid, ...question }) => question) } : step,
  );

export const newStep = (type: OnboardingBlock["type"]): DraftStep => freshSteps([newBlock(type)])[0];

export const newQuestion = (question: Partial<Question> = {}): DraftQuestion => ({
  label: "", type: "text", required: true, ...question, uid: newUid(),
});

/** A questions step again, as new: no id, and questions without keys, so their answers start empty. */
export function duplicate(step: DraftQuestions): DraftQuestions {
  const { id, uid, ...rest } = step;
  return { ...structuredClone(rest), uid: newUid(), fields: step.fields.map(({ key, ...q }) => ({ ...structuredClone(q), uid: newUid() })) };
}

export const isFull = (steps: DraftStep[]) => steps.length >= MAX_BLOCKS;

export const moved = <T,>(list: T[], from: number, to: number): T[] => {
  const next = [...list];
  next.splice(to, 0, ...next.splice(from, 1));
  return next;
};
